import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

export default function AdminContactThread() {
  const nav = useNavigate();
  const { threadId = "" } = useParams();
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";

  const [threadMeta, setThreadMeta] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!passcode || !threadId) return;
    setLoading(true);
    try {
      const { getAdminContactConversation } = await import("../../lib/api");
      const data = await getAdminContactConversation(threadId, passcode);
      setThreadMeta(data?.thread || null);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      setError("");
    } catch (_) {
      setError("Failed to load conversation.");
    } finally {
      setLoading(false);
    }
  }, [passcode, threadId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const onDelete = async () => {
    if (!threadId || !passcode) return;
    const ok = window.confirm("Delete this conversation and all messages permanently?");
    if (!ok) return;
    try {
      const { deleteAdminContactConversation } = await import("../../lib/api");
      await deleteAdminContactConversation(threadId, passcode);
      nav(`/admin/contact?passcode=${encodeURIComponent(passcode)}`);
    } catch (_) {
      setError("Failed to delete conversation.");
    }
  };

  const onSend = async (e) => {
    e.preventDefault();
    const message = (draft || "").trim();
    if (!message || !threadId || !passcode) return;
    setSending(true);
    try {
      const { adminReplyContactConversation } = await import("../../lib/api");
      await adminReplyContactConversation(threadId, message, passcode);
      setDraft("");
      await load();
    } catch (_) {
      setError("Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10" data-testid="admin-contact-thread-page">
      <Card className="border border-emerald-300/70 dark:border-emerald-700/50 bg-white dark:bg-slate-900 shadow-[0_10px_28px_rgba(16,185,129,0.10)]">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-slate-900 dark:text-slate-100" data-testid="admin-contact-thread-title">Conversation</CardTitle>
            <Button variant="outline" onClick={() => nav(`/admin/contact?passcode=${encodeURIComponent(passcode)}`)} data-testid="admin-contact-thread-back-button">Back to list</Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {!!error && <div className="text-sm text-red-600" data-testid="admin-contact-thread-error">{error}</div>}
          {loading && <div className="text-sm text-slate-500">Loading…</div>}

          {!loading && (
            <div className="rounded-xl border border-emerald-300/70 dark:border-emerald-700/50 p-3 bg-emerald-50/35 dark:bg-slate-950/35" data-testid="admin-contact-thread-window">
              <div className="flex items-center justify-between gap-3 border-b border-emerald-300/70 dark:border-emerald-700/40 pb-3" data-testid="admin-contact-thread-header">
                <div>
                  <div className="font-semibold text-sm text-slate-900 dark:text-slate-100" data-testid="admin-contact-thread-name">{threadMeta?.name || "Conversation"}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300" data-testid="admin-contact-thread-meta">
                    {threadMeta?.messageCount || 0} messages · Updated {threadMeta?.updatedAt ? new Date(threadMeta.updatedAt).toLocaleString() : "-"}
                  </div>
                </div>
                <button className="px-2 py-1 text-xs rounded border border-red-500 bg-red-700 text-white hover:bg-red-800" onClick={onDelete} data-testid="admin-contact-delete-thread-button">Delete</button>
              </div>

              <div className="mt-3 h-[44vh] overflow-auto space-y-3" data-testid="admin-contact-message-list">
                {messages.length === 0 && <div className="text-sm text-slate-500">No messages yet.</div>}
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`} data-testid={`admin-contact-message-${msg.id}`}>
                    <div className="max-w-[82%] rounded-2xl px-3 py-2 bg-emerald-100/80 dark:bg-emerald-900/25 border border-emerald-300/70 dark:border-emerald-700/40">
                      <div className="text-xs text-slate-700 dark:text-slate-300">{msg.name}</div>
                      <div className="text-sm whitespace-pre-wrap text-slate-900 dark:text-slate-100">{msg.message}</div>
                      <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">{new Date(msg.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={onSend} className="mt-3 pt-3 border-t border-emerald-300/70 dark:border-emerald-700/40" data-testid="admin-contact-reply-form">
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50/70 dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Write a reply..."
                  data-testid="admin-contact-reply-input"
                />
                <div className="mt-2 flex justify-end">
                  <Button type="submit" disabled={sending || !draft.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-contact-reply-send-button">
                    {sending ? "Sending..." : "Send Reply"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
